import { http } from '@/lib/ats-https';
import type {
  BackgroundCheckPackagesResponse,
  SendBackgroundCheckPayload,
  SendBackgroundCheckResponse,
} from './backgroundCheckTypes';

// Credit management settings page — admins are sent here to top up when a
// fraud-detector check is blocked by insufficient credits.
export const CREDIT_MANAGEMENT_PATH = '/settings/credit-management';

// Certn
export const getCertnPackages =
  async (): Promise<BackgroundCheckPackagesResponse> => {
    const response = await http.get('/certn/packages');
    return response as unknown as BackgroundCheckPackagesResponse;
  };

export const sendCertnBackgroundCheck = async (
  payload: SendBackgroundCheckPayload
): Promise<SendBackgroundCheckResponse> => {
  const response = await http.post('/certn/order_case', payload);
  return response as unknown as SendBackgroundCheckResponse;
};

// Zinc
export const getZincPackages =
  async (): Promise<BackgroundCheckPackagesResponse> => {
    const response = await http.get('/zinc/packages');
    return response as unknown as BackgroundCheckPackagesResponse;
  };

export const sendZincBackgroundCheck = async (
  payload: SendBackgroundCheckPayload
): Promise<SendBackgroundCheckResponse> => {
  const response = await http.post('/zinc/create_check', payload);
  return response as unknown as SendBackgroundCheckResponse;
};

// Fraud Detector
export const notifyAdminCredits = async (): Promise<void> => {
  await http.post(
    '/contact_background_verification/request_credit_cap_notification',
    {}
  );
};

export interface CreditCapError {
  errors: string[];
  error_code: string;
  cap_status: {
    exceeded: boolean;
    count: number;
    cap: number;
    remaining: number;
  };
  user_has_manage_org_fraud_detector: boolean;
  reminder_sent_recently: boolean;
}

const CREDIT_CAP_ERROR_CODES = new Set([
  'CREDIT_CAP_EXCEEDED',
  'err_credit_cap_exceeded',
  'err_bulk_credit_cap_exceeded',
]);

export const isCreditCapError = (error: unknown): boolean => {
  const errorCode =
    (error as any)?.error_code ??
    (error as any)?.response?.data?.error_code ??
    (error as any)?.errors?.[0];
  return typeof errorCode === 'string' && CREDIT_CAP_ERROR_CODES.has(errorCode);
};

export const getCreditCapData = (error: unknown): CreditCapError =>
  (error as any)?.error_code
    ? (error as CreditCapError)
    : (error as any)?.response?.data;

export const getCreditCapDataFromValidation = (data: any): CreditCapError => ({
  errors: [],
  error_code: data?.error_code,
  cap_status: data?.cap_status,
  user_has_manage_org_fraud_detector:
    data?.credit_cap_notification?.user_has_manage_org_fraud_detector,
  reminder_sent_recently: data?.credit_cap_notification?.reminder_sent_recently,
});

export interface BulkValidationApplication {
  id: number;
  contact: {
    id: number;
    first_name: string;
    last_name: string;
    image_url: string | null;
    position: string | null;
  };
}

export interface BulkValidationInvalidGroup {
  reason: string;
  ats_applications: BulkValidationApplication[];
}

export interface BulkValidationData {
  eligible_ats_applications: BulkValidationApplication[];
  invalid_ats_applications: BulkValidationInvalidGroup[];
  eligible_contacts: BulkValidationApplication[];
  invalid_contacts: BulkValidationInvalidGroup[];
}

export const validateBulkFraudDetectorVerification = async (payload: {
  ats_job_id: number;
  ats_application_ids: number[];
}): Promise<BulkValidationData> => {
  const response = await http.post(
    '/contact_background_verification/verifications/bulk/validation',
    payload
  );
  return (response as any)?.data as BulkValidationData;
};

export const sendBulkFraudDetectorVerification = async (payload: {
  ats_job_id: number;
  ats_application_ids: number[];
  verification_tier: string;
}): Promise<void> => {
  const response = await http.post(
    '/contact_background_verification/verifications/bulk',
    payload
  );
  if ((response as any)?.error_code) throw response;
};

export const sendFraudDetectorVerification = async (payload: {
  ats_application_id: string;
  ats_job_id: number;
  verification_tier: string;
}): Promise<SendBackgroundCheckResponse> => {
  const response = await http.post(
    `/ats_applications/${payload.ats_application_id}/contact_background_verification/verifications`,
    {
      ats_job_id: payload.ats_job_id,
      verification_tier: payload.verification_tier,
    }
  );
  if ((response as any)?.error_code) throw response;
  return response as unknown as SendBackgroundCheckResponse;
};
